import api from './api.service';

export type RagStatus = 'red' | 'amber' | 'green';

export interface HealthDimensionScore {
  key: string;
  label: string;
  score: number;
  target: number;
  actual: number;
  trend: 'up' | 'down' | 'flat';
  status: RagStatus;
  confidence: number;
  drivers: string[];
  recommended_actions: Array<{ title: string; description: string; priority: string; linked_route?: string }>;
}

export interface BusinessHealthResult {
  composite_score: number;
  composite_status: RagStatus;
  score_date: string;
  refreshed_at: string;
  dimensions: HealthDimensionScore[];
  top_drivers: Array<{ rank: number; message: string; dimension: string; severity: string }>;
  recommended_actions: Array<{ title: string; description: string; priority: string; linked_route?: string }>;
}

export interface CommandCenterResult {
  business_health: BusinessHealthResult;
  widgets: Array<{
    key: string;
    label: string;
    value: number | string;
    unit?: string;
    status?: RagStatus;
    trend?: 'up' | 'down' | 'flat';
    delta_pct?: number | null;
    sparkline?: number[];
    link?: string;
  }>;
  worry_about_today: Array<{ area: string; metric: string; count: number; severity: string; message: string }>;
  money_stuck: Array<{ category: string; amount: number; note?: string }>;
  refreshed_at: string;
}

export type FinancialDataSource = 'ledger' | 'estimate' | 'hybrid';

export interface ComparativeLine {
  key: string;
  label: string;
  amount: number;
  pct_of_revenue?: number | null;
  indent?: number;
  is_total?: boolean;
}

export interface FinancialHealthPeriod {
  fiscal_year_label: string;
  from_date: string;
  to_date: string;
  effective_to_date: string;
  net_profit: number;
  net_profit_margin_pct: number;
  cash_balance: number;
  revenue_total: number;
  pl_lines: ComparativeLine[];
  bs_lines: ComparativeLine[];
  sources: Record<string, FinancialDataSource>;
  data_quality?: string[];
}

export interface FinancialHealthResult {
  fiscal_year_start_month: number;
  base_currency: string;
  periods: FinancialHealthPeriod[];
  yoy: {
    net_profit_pct?: number | null;
    cash_pct?: number | null;
    revenue_pct?: number | null;
  };
  refreshed_at: string;
}

export type GroupCompanyKpis = {
  sales_today: number | null;
  sales_mtd: number | null;
  gross_profit_ytd: number | null;
  gross_margin_pct: number | null;
  net_profit: number | null;
  net_margin_pct: number | null;
  cash_and_bank: number | null;
  receivables: number | null;
  overdue_ar_amount: number | null;
  overdue_ar_count: number | null;
  payables: number | null;
  inventory_value: number | null;
  slow_moving_count: number | null;
  dead_stock_count: number | null;
  inventory_turns_365: number | null;
  inventory_dio_days: number | null;
  stock_count_accuracy_pct: number | null;
  stock_count_posted_90d: number | null;
  working_capital: number | null;
  order_backlog: number | null;
  pipeline: number | null;
  approvals_backlog: number | null;
  delivered_not_invoiced: number | null;
  grn_not_invoiced_count: number | null;
  open_po_value: number | null;
  project_overrun_count: number | null;
  project_count: number | null;
  projects_negative_margin_count: number | null;
  project_material_balance: number | null;
  ic_net_position: number | null;
  hr_headcount: number | null;
  hr_payroll_cost_mtd: number | null;
  hr_ot_hours_mtd: number | null;
  hr_ot_pct_mtd: number | null;
  hr_leave_liability_days: number | null;
  hr_eosb_provision: number | null;
  hr_doc_expiry_open: number | null;
};

export type GroupTopProject = {
  project_id: string;
  project_number: string;
  project_name: string;
  eac: number | null;
  billed_profit: number | null;
  billed_margin_pct: number | null;
  any_bva_over: boolean;
};

export type GroupCompanyRow = {
  company_id: string;
  company_name: string;
  company_code: string;
  currency: string;
  is_parent: boolean;
  status: 'ok' | 'error';
  error?: string;
  permissions_scope: 'company_membership' | 'active_jwt_fallback';
  kpis: GroupCompanyKpis;
  kpis_reporting?: Partial<GroupCompanyKpis> | null;
  top_projects: GroupTopProject[];
  attention: Array<{
    area: string;
    severity: 'critical' | 'warning' | 'info';
    message: string;
    link?: string;
  }>;
  drill_links: {
    owner_intelligence: string;
    cash: string;
    profit: string;
    projects: string;
    finance_ar: string;
  };
};

export type GroupGmDashboardResult = {
  as_of: string;
  group_definition: string;
  active_company_id: string;
  companies: GroupCompanyRow[];
  group: {
    reporting_currency: string;
    currency_mode: 'single' | 'mixed' | 'converted';
    currency: string | null;
    company_count: number;
    ok_count: number;
    error_count: number;
    totals: Partial<GroupCompanyKpis> | null;
    fx_converted_company_count?: number;
    notes: string[];
  };
  attention_queues: Array<{
    company_id: string;
    company_name: string;
    area: string;
    severity: 'critical' | 'warning' | 'info';
    message: string;
    link?: string;
  }>;
};

const unwrap = <T>(res: { data: { data?: T } | T }): T => {
  const d = res.data as { data?: T };
  return (d.data ?? res.data) as T;
};

export const ownerIntelligenceService = {
  getCommandCenter: () => api.getClient().get('/owner-intelligence/command-center').then(unwrap<CommandCenterResult>),
  getGroupDashboard: () =>
    api.getClient().get('/owner-intelligence/group-dashboard').then(unwrap<GroupGmDashboardResult>),
  getGroupDashboardCompany: (companyId: string) =>
    api
      .getClient()
      .get(`/owner-intelligence/group-dashboard/company/${companyId}`)
      .then(unwrap<GroupCompanyRow>),
  getBusinessHealth: () => api.getClient().get('/owner-intelligence/business-health').then(unwrap<BusinessHealthResult>),
  refreshSnapshots: () => api.getClient().post('/owner-intelligence/snapshots/refresh').then(unwrap),
  listKpiSnapshots: (periodType?: string) =>
    api.getClient().get('/owner-intelligence/kpi/snapshots', { params: { period_type: periodType } }).then(unwrap),
  listKpiCatalog: () => api.getClient().get('/owner-intelligence/kpi/catalog').then(unwrap),
  getKpiDrillDown: (kpiCode: string) => api.getClient().get(`/owner-intelligence/kpi/drill-down/${kpiCode}`).then(unwrap),
  getNarrative: (params: { kpi_code: string; variance_pct?: number; actual?: number }) =>
    api.getClient().get('/owner-intelligence/narrative', { params }).then(unwrap),
  getStrategyAlignment: () => api.getClient().get('/owner-intelligence/strategy-alignment').then(unwrap),
  getDataQuality: () => api.getClient().get('/owner-intelligence/data-quality').then(unwrap),
  getGrowthProfit: () => api.getClient().get('/owner-intelligence/growth-profit').then(unwrap),
  getFinancialHealth: (years = 2) =>
    api.getClient().get('/owner-intelligence/financial-health', { params: { years } }).then(unwrap<FinancialHealthResult>),
  getCash: () => api.getClient().get('/owner-intelligence/cash').then(unwrap),
  getCustomers: () => api.getClient().get('/owner-intelligence/customers').then(unwrap),
  getOperations: () => api.getClient().get('/owner-intelligence/operations').then(unwrap),
  getSupplyChain: () => api.getClient().get('/owner-intelligence/supply-chain').then(unwrap),
  getRisk: () => api.getClient().get('/owner-intelligence/risk').then(unwrap),
  listAlerts: (status?: string) => api.getClient().get('/owner-intelligence/alerts', { params: { status } }).then(unwrap),
  listAlertThresholds: () => api.getClient().get('/owner-intelligence/alert-thresholds').then(unwrap),
  upsertAlertThreshold: (body: Record<string, unknown>) => api.getClient().put('/owner-intelligence/alert-thresholds', body).then(unwrap),
  listActions: (status?: string) => api.getClient().get('/owner-intelligence/actions', { params: { status } }).then(unwrap),
  createAction: (body: Record<string, unknown>) => api.getClient().post('/owner-intelligence/actions', body).then(unwrap),
  listDecisions: () => api.getClient().get('/owner-intelligence/decisions').then(unwrap),
  listScenarios: () => api.getClient().get('/owner-intelligence/scenarios').then(unwrap),
  createScenario: (body: Record<string, unknown>) => api.getClient().post('/owner-intelligence/scenarios', body).then(unwrap),
  resolveAlert: (id: string) => api.getClient().post(`/owner-intelligence/alerts/${id}/resolve`).then(unwrap),
  updateActionStatus: (id: string, body: { status: string; actual_impact?: string }) =>
    api.getClient().patch(`/owner-intelligence/actions/${id}/status`, body).then(unwrap),
  assignAction: (id: string, ownerUserId: string) =>
    api.getClient().patch(`/owner-intelligence/actions/${id}/assign`, { owner_user_id: ownerUserId }).then(unwrap),
  escalateAction: (id: string, escalateToUserId: string) =>
    api.getClient().patch(`/owner-intelligence/actions/${id}/escalate`, { escalate_to_user_id: escalateToUserId }).then(unwrap),
  reopenAction: (id: string) => api.getClient().post(`/owner-intelligence/actions/${id}/reopen`).then(unwrap),
  listActionComments: (id: string) => api.getClient().get(`/owner-intelligence/actions/${id}/comments`).then(unwrap),
  addActionComment: (id: string, comment: string) =>
    api.getClient().post(`/owner-intelligence/actions/${id}/comments`, { comment }).then(unwrap),
  createDecision: (body: Record<string, unknown>) => api.getClient().post('/owner-intelligence/decisions', body).then(unwrap),
  addDecisionComment: (id: string, comment: string) =>
    api.getClient().post(`/owner-intelligence/decisions/${id}/comments`, { comment }).then(unwrap),
  updateDecisionImpact: (id: string, actualImpact: string) =>
    api.getClient().patch(`/owner-intelligence/decisions/${id}/impact`, { actual_impact: actualImpact }).then(unwrap),
  addDecisionAttachment: (id: string, body: Record<string, unknown>) =>
    api.getClient().post(`/owner-intelligence/decisions/${id}/attachments`, body).then(unwrap),
  compareScenarios: (scenarioIds: string[]) =>
    api.getClient().post('/owner-intelligence/scenarios/compare', { scenario_ids: scenarioIds }).then(unwrap),
  upsertKpiTarget: (body: Record<string, unknown>) => api.getClient().put('/owner-intelligence/kpi/targets', body).then(unwrap),
  generateBrief: () => api.getClient().post('/owner-intelligence/briefs/generate').then(unwrap),
  listBriefs: () => api.getClient().get('/owner-intelligence/briefs').then(unwrap),
  getChangeSince: (since?: string) => api.getClient().get('/owner-intelligence/change-since', { params: { since } }).then(unwrap),
  listBoardPacks: () => api.getClient().get('/owner-intelligence/board-packs').then(unwrap),
  createBoardPack: (body: Record<string, unknown>) => api.getClient().post('/owner-intelligence/board-packs', body).then(unwrap),
  getRootCause: (topic?: string) => api.getClient().get('/owner-intelligence/root-cause', { params: { topic } }).then(unwrap),
  getBenchmarks: () => api.getClient().get('/owner-intelligence/benchmarks/customers').then(unwrap),
  exportBoardPackPdf: async (packId: string) => {
    const res = await api.getClient().get(`/owner-intelligence/board-packs/${packId}/export/pdf`, { responseType: 'blob' });
    return res.data as Blob;
  },
  approveBoardPack: (id: string) => api.getClient().post(`/owner-intelligence/board-packs/${id}/approve`).then(unwrap),
  exportBrief: async (briefId: string) => {
    const res = await api.getClient().get(`/owner-intelligence/briefs/${briefId}/export`, { responseType: 'blob' });
    return res.data as Blob;
  },
  listMeetings: () => api.getClient().get('/owner-intelligence/meetings').then(unwrap),
  createMeeting: (body: Record<string, unknown>) => api.getClient().post('/owner-intelligence/meetings', body).then(unwrap),
  askAdvisor: (question: string) => api.getClient().post('/owner-intelligence/advisor/ask', { question }).then(unwrap),
  listAuditLog: () => api.getClient().get('/owner-intelligence/audit-log').then(unwrap),
  getExternalContext: () => api.getClient().get('/owner-intelligence/external-context').then(unwrap),
  listExternalSources: () => api.getClient().get('/owner-intelligence/external-context/sources').then(unwrap),
  upsertExternalSource: (body: Record<string, unknown>) => api.getClient().put('/owner-intelligence/external-context/sources', body).then(unwrap),
  refreshExternalContext: (sourceId?: string) =>
    sourceId
      ? api.getClient().post(`/owner-intelligence/external-context/sources/${sourceId}/refresh`).then(unwrap)
      : api.getClient().post('/owner-intelligence/external-context/refresh').then(unwrap),
  listAccessPolicies: () => api.getClient().get('/owner-intelligence/access-policies').then(unwrap),
  upsertAccessPolicy: (body: Record<string, unknown>) => api.getClient().put('/owner-intelligence/access-policies', body).then(unwrap),
  getMyAccessPolicy: () => api.getClient().get('/owner-intelligence/access-policies/me').then(unwrap),
  exportBoardPackPptx: async (packId: string) => {
    const res = await api.getClient().get(`/owner-intelligence/board-packs/${packId}/export/pptx`, { responseType: 'blob' });
    return res.data as Blob;
  },
};

export default ownerIntelligenceService;
